import { IAttractionReservationFormData, playerTypesData } from "@app-components/attractions-reservation-form/attractions-reservation-form";
import { IReservationFormData, IReservationFormDataBowlers } from "@app-components/reservation-form/reservation-form";
import type { IPlayersQuantity, IPlayersTypes } from "@app-rest/systems.interfaces";
import router from "@app-router/index";
import useSystemStore from "@app-store/system";
import { ref } from "vue";

import { dateKey, dateTimeKey, stringToDateUTC } from "./dates";

type FormDataType<T extends "bowling" | "attraction"> = T extends "bowling"
  ? IReservationFormData
  : IAttractionReservationFormData;


function parsePlayersQueryString(playersQueryString: string | undefined, playerTypesData: IPlayersTypes | null): IPlayersQuantity[] {
	return playersQueryString?.split(",").map(playerStr => {
		  const parts = playerStr.split("-");
		  const id = parts[0];
		  if (!id || isNaN(+id)) return false;

		  const q = parts[1];
		  const pTypeFromQs: IReservationFormDataBowlers = {
			Id: +id,
			Quantity: !q || isNaN(+q) ? 1 : +q
		  };

		  const pType = playerTypesData?.Types.find(t => t.Id === +id);
		  if (!pType) return false;

		  return {
			...pType,
			...pTypeFromQs
		  };
	}).filter(Boolean) as IPlayersQuantity[] ?? [];
}

export function parseQueryStringFormData<T extends "bowling" | "attraction" = "bowling">(type?: T) {
	const appRouter = ref(router);
	const route = appRouter.value.currentRoute;
	const formDataTypes = {
		bowling: {
			datetime: null,
			bowlers: []
		},
		attraction: {
			attraction: null,
			date: null,
			datetime: null,
			players: []
		}
	};
	const formData = formDataTypes[type ?? "bowling"] as FormDataType<T>;

	const datetime = route.query.q_datetime as string | undefined;
	formData.datetime = datetime ? stringToDateUTC(datetime) ?? null : null;

	if("date" in formData) {
		const date = route.query.q_date as string | undefined;
		formData.date = date ? stringToDateUTC(date) ?? null : null;
	}

	if ("bowlers" in formData) {
		const bowlers = route.query.q_bowlers as string | undefined;
		formData.bowlers = parsePlayersQueryString(bowlers, useSystemStore().playerTypes);
	}

	if ("players" in formData) {
		const players = route.query.q_players as string | undefined;
		formData.players = parsePlayersQueryString(players, {...playerTypesData});
	}

	if("attraction" in formData) {
		const attraction = route.query.q_attraction as string | undefined;
		formData.attraction = attraction ? JSON.parse(decodeURIComponent(route.query.q_attraction as string)) : null;
	}

	return formData;
}

export function getQueryStringFormData(formData: IReservationFormData | IAttractionReservationFormData) {
	const appRouter = ref(router);
	const route = appRouter.value.currentRoute;
	const query = { ...route.query };

	if ("date" in formData) {
		delete query.q_date;
		if (formData.date)
			query.q_date = dateKey(formData.date);
	}

	delete query.q_datetime;
	if (formData.datetime)
		query.q_datetime = dateTimeKey(formData.datetime);

	if ("bowlers" in formData) {
		delete query.q_bowlers;
		if (formData.bowlers.length)
			query.q_bowlers = formData.bowlers.map(b => `${b.Id}-${b.Quantity}`).join(",");
	}

	if ("attraction" in formData) {
		delete query.q_attraction;
		if (formData.attraction)
			query.q_attraction = encodeURIComponent(JSON.stringify(formData.attraction));
	}

	if ("players" in formData) {
		delete query.q_players;
		if (formData.players.length)
			query.q_players = formData.players.map(b => `${b.Id}-${b.Quantity}`).join(",");
	}

	return query;
}

export function clearedQueryStringFormData() {
	const appRouter = ref(router);
	const route = appRouter.value.currentRoute;
	const query = { ...route.query };
	Object.keys(query).forEach(key => {
		if (key.indexOf("q_") === 0)
			delete query[key];
	});
	return query;
}
